import React, { useEffect, useState, useCallback } from "react";
import { RefreshCcw, Search, Moon, Users, TrendingUp, CalendarRange, BarChart3 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const monthLabel = (ym) => {
    const [y, m] = (ym || "").split("-");
    const idx = Number(m) - 1;
    return idx >= 0 && idx < 12 ? `${MONTHS_TR[idx]} ${y}` : ym;
};
const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso || "—"; } };
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso || "—"; } };

const SOURCE_TR = { home: "Ana Sayfa", home_search: "Ana Sayfa (Ara)", hotel_detail: "Otel Detay" };

const KPI = ({ icon: Icon, label, value, sub }) => (
    <div className="border border-border_green bg-emerald_surface p-5">
        <div className="flex items-center justify-between">
            <Icon className="w-5 h-5 text-gold" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted_green text-right">{label}</span>
        </div>
        <div className="mt-4 font-serif text-3xl">{value}</div>
        {sub && <div className="text-[11px] text-muted_green mt-1">{sub}</div>}
    </div>
);

const AdminAnalytics = () => {
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (d) => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/analytics/searches?days=${d}`);
            setData(res.data);
        } catch { toast.error("Analitik verileri yüklenemedi"); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(days); }, [days, load]);

    const maxMonth = Math.max(1, ...(data?.demand_by_month || []).map((m) => m.count));
    const maxGuests = Math.max(1, ...(data?.guests_distribution || []).map((g) => g.count));

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Arama Analitiği</h1>
                    <p className="text-muted_green text-sm">Kullanıcıların arattığı umre tarihlerini ve talep yoğunluğunu inceleyin.</p>
                </div>
                <div className="flex items-center gap-2">
                    {[30, 90, 365].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${days === d ? "bg-gold text-emerald_deep border-gold" : "border-border_green text-muted_green hover:border-gold hover:text-gold"}`}
                            data-testid={`period-${d}`}
                        >
                            {d === 365 ? "1 Yıl" : `${d} Gün`}
                        </button>
                    ))}
                    <button onClick={() => load(days)} className="btn-outline-gold !text-xs" data-testid="refresh-analytics"><RefreshCcw className="w-3.5 h-3.5" /></button>
                </div>
            </div>

            {loading && !data ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : !data ? null : (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <KPI icon={Search} label="Toplam Arama" value={data.total_searches} sub={`Son ${days} gün`} />
                        <KPI icon={Moon} label="Ort. Gece" value={data.avg_nights} sub="Aranan ortalama konaklama" />
                        <KPI icon={Users} label="Ort. Misafir" value={data.avg_guests} sub="Arama başına kişi" />
                        <KPI icon={TrendingUp} label="Dönüşüm" value={`%${data.conversion_percent}`} sub={`${data.reservations_in_period} rezervasyon / ${data.total_searches} arama`} />
                    </div>

                    <div className="grid lg:grid-cols-2 gap-4 mb-6">
                        {/* Demand by check-in month */}
                        <div className="border border-border_green bg-emerald_surface">
                            <div className="px-6 py-4 border-b border-border_green flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-gold" />
                                <h2 className="font-serif text-xl">Aylara Göre Talep (Giriş Tarihi)</h2>
                            </div>
                            <div className="p-6 space-y-3" data-testid="demand-by-month">
                                {data.demand_by_month.length === 0 && <div className="text-muted_green text-sm">Henüz arama verisi yok.</div>}
                                {data.demand_by_month.map((m) => (
                                    <div key={m.month}>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="text-bone">{monthLabel(m.month)}</span>
                                            <span className="text-muted_green">{m.count} arama · ort. {m.avg_nights} gece · {m.avg_guests} kişi</span>
                                        </div>
                                        <div className="h-2.5 bg-emerald_deep border border-border_green">
                                            <div className="h-full bg-gold" style={{ width: `${(m.count / maxMonth) * 100}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Top searched ranges */}
                        <div className="border border-border_green bg-emerald_surface">
                            <div className="px-6 py-4 border-b border-border_green flex items-center gap-2">
                                <CalendarRange className="w-4 h-4 text-gold" />
                                <h2 className="font-serif text-xl">En Çok Aranan Tarih Aralıkları</h2>
                            </div>
                            <div className="divide-y divide-border_green" data-testid="top-ranges">
                                {data.top_ranges.length === 0 && <div className="p-6 text-muted_green text-sm">Henüz veri yok.</div>}
                                {data.top_ranges.slice(0, 10).map((r, i) => (
                                    <div key={i} className="px-6 py-3 flex items-center justify-between gap-3 text-sm">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className="font-mono text-gold text-xs w-5">{i + 1}.</span>
                                            <span className="truncate">{fmtD(r.check_in)} → {fmtD(r.check_out)}</span>
                                        </div>
                                        <div className="flex items-center gap-4 flex-shrink-0 text-xs text-muted_green">
                                            <span>{r.nights} gece</span>
                                            <span className="text-gold font-mono">{r.count}×</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-4">
                        {/* Guests distribution */}
                        <div className="border border-border_green bg-emerald_surface">
                            <div className="px-6 py-4 border-b border-border_green">
                                <h2 className="font-serif text-xl">Misafir Sayısı Dağılımı</h2>
                            </div>
                            <div className="p-6 space-y-3">
                                {data.guests_distribution.length === 0 && <div className="text-muted_green text-sm">Veri yok.</div>}
                                {data.guests_distribution.map((g) => (
                                    <div key={g.guests}>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span>{g.guests} kişi</span>
                                            <span className="text-muted_green">{g.count} arama</span>
                                        </div>
                                        <div className="h-2 bg-emerald_deep border border-border_green">
                                            <div className="h-full bg-emerald-500/80" style={{ width: `${(g.count / maxGuests) * 100}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent searches */}
                        <div className="lg:col-span-2 border border-border_green bg-emerald_surface">
                            <div className="px-6 py-4 border-b border-border_green">
                                <h2 className="font-serif text-xl">Son Aramalar</h2>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="recent-searches-table">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted_green">
                                            <th className="px-5 py-2.5">Zaman</th>
                                            <th className="px-5 py-2.5">Giriş → Çıkış</th>
                                            <th className="px-5 py-2.5">Gece</th>
                                            <th className="px-5 py-2.5">Misafir</th>
                                            <th className="px-5 py-2.5">Şehir</th>
                                            <th className="px-5 py-2.5">Kaynak</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border_green">
                                        {data.recent.length === 0 && (
                                            <tr><td colSpan={6} className="px-5 py-6 text-center text-muted_green">Henüz arama kaydı yok.</td></tr>
                                        )}
                                        {data.recent.map((l) => (
                                            <tr key={l.id}>
                                                <td className="px-5 py-2.5 text-xs text-muted_green whitespace-nowrap">{fmtDT(l.created_at)}</td>
                                                <td className="px-5 py-2.5 whitespace-nowrap">{fmtD(l.check_in)} → {fmtD(l.check_out)}</td>
                                                <td className="px-5 py-2.5">{l.nights}</td>
                                                <td className="px-5 py-2.5">{l.guests}</td>
                                                <td className="px-5 py-2.5 text-muted_green">{l.city === "Mecca" ? "Mekke" : l.city === "Medina" ? "Medine" : "—"}</td>
                                                <td className="px-5 py-2.5 text-xs text-gold">{SOURCE_TR[l.source] || l.source}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminAnalytics;
